import { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth, useAuthActions } from '../state/AuthContext';
import { useDispatch } from '../state/AppContext';
import { VIEW_UNIVERSE, VIEW_UNIVERSE_PREVIEW } from '../state/actions';
import { renderUniverseMiniViz } from '../galaxy/universeMiniViz';
import { rankSpotlightRecommendations, findSpotlightByName } from '../utils/spotlightRanking';
import SpotlightCard from './SpotlightCard';
import '../styles/universe.css';
import '../styles/universe-preview.css';

function MyUniverseSection({ visible }) {
  const { user, favorites, discoveredArtists, knownArtists, dislikes, universeData, universeStatus } = useAuth();
  const { refreshUniverse } = useAuthActions();
  const appDispatch = useDispatch();
  const canvasRef = useRef(null);

  const handleExplore = useCallback(() => {
    appDispatch({ type: VIEW_UNIVERSE });
  }, [appDispatch]);

  // Render mini visualization when data is available and component is visible
  useEffect(() => {
    if (!visible || !universeData?.visualization || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;

    const cleanup = renderUniverseMiniViz(canvas, universeData);
    return cleanup;
  }, [universeData, visible]);

  // Auto-trigger compute when stale, but only while the user is off this
  // tab. Recomputing in-place would re-render the visualization and yank
  // the spotlight mid-browse; deferring until they navigate away lets
  // them keep rating the spotlight artists without interruption.
  useEffect(() => {
    if (user && !visible && universeStatus === 'stale') {
      refreshUniverse();
    }
  }, [user, visible, universeStatus, refreshUniverse]);

  // Animated loading percentage for the computing state
  const [computePercent, setComputePercent] = useState(0);
  useEffect(() => {
    if (universeStatus !== 'computing') {
      setComputePercent(0);
      return;
    }
    const start = Date.now();
    const timer = setInterval(() => {
      const elapsed = (Date.now() - start) / 1000;
      // Asymptotic curve: reaches ~90% at 30s, never quite hits 100
      const pct = Math.min(Math.round(95 * (1 - Math.exp(-elapsed / 15))), 95);
      setComputePercent(pct);
    }, 500);
    return () => clearInterval(timer);
  }, [universeStatus]);

  // Ranked list of unrated recommendations. Rating an artist drops them
  // from this list but the spotlight keeps showing the current pick
  // until the user explicitly presses Next.
  const rankedSpotlights = useMemo(() => {
    if (!universeData?.clusters) return [];
    const excluded = new Set(
      [...favorites, ...discoveredArtists, ...knownArtists, ...dislikes]
        .map((a) => a.artistName?.toLowerCase().trim())
        .filter(Boolean)
    );
    return rankSpotlightRecommendations(universeData, excluded);
  }, [universeData, favorites, discoveredArtists, knownArtists, dislikes]);

  // Which artist the spotlight is currently showing. Advances only on
  // Next; artist actions leave it alone so the user can see their rating
  // apply to the pills without the card jumping.
  const [currentSpotlightName, setCurrentSpotlightName] = useState(null);

  // Names already shown this session, so Next walks down the ranked list
  // without revisiting earlier picks. Reset when the tab is re-entered.
  const [shownSpotlightNames, setShownSpotlightNames] = useState(() => new Set());
  const prevVisibleRef = useRef(visible);
  useEffect(() => {
    if (visible && !prevVisibleRef.current) {
      setShownSpotlightNames(new Set());
    }
    prevVisibleRef.current = visible;
  }, [visible]);

  // Resolve the currently-displayed spotlight. Prefer the held name so
  // rated artists stay visible; fall back to the top of the ranked list.
  const displayedSpotlight = useMemo(() => {
    if (!universeData?.clusters) return null;
    if (currentSpotlightName) {
      const found = findSpotlightByName(universeData, currentSpotlightName);
      if (found) return found;
    }
    return rankedSpotlights[0] || null;
  }, [universeData, currentSpotlightName, rankedSpotlights]);

  // Seed the held name from the top of the ranked list on first load,
  // and recover if the held artist disappears from the universe (rare).
  useEffect(() => {
    if (!displayedSpotlight) return;
    if (displayedSpotlight.recommendation.name !== currentSpotlightName) {
      setCurrentSpotlightName(displayedSpotlight.recommendation.name);
    }
  }, [displayedSpotlight, currentSpotlightName]);

  // Is there an unrated candidate further down the ranked list we haven't
  // shown yet? Skip the current pick and anything already seen this session.
  const nextSpotlight = useMemo(() => {
    if (rankedSpotlights.length === 0) return null;
    const currentLower = currentSpotlightName?.toLowerCase().trim();
    return (
      rankedSpotlights.find((r) => {
        const nameLower = r.recommendation.name.toLowerCase().trim();
        return nameLower !== currentLower && !shownSpotlightNames.has(nameLower);
      }) || null
    );
  }, [rankedSpotlights, currentSpotlightName, shownSpotlightNames]);

  // Crossfade when the user presses Next: fade the outgoing artist out,
  // then mount the incoming one with its own fade-in animation.
  const [pendingSpotlight, setPendingSpotlight] = useState(null);
  const leaving = pendingSpotlight !== null;
  const handleSpotlightNext = useCallback(() => {
    if (!nextSpotlight || leaving) return;
    if (currentSpotlightName) {
      const seen = currentSpotlightName.toLowerCase().trim();
      setShownSpotlightNames((prev) => {
        if (prev.has(seen)) return prev;
        const next = new Set(prev);
        next.add(seen);
        return next;
      });
    }
    setPendingSpotlight(nextSpotlight);
  }, [nextSpotlight, leaving, currentSpotlightName]);
  useEffect(() => {
    if (!pendingSpotlight) return;
    const target = pendingSpotlight.recommendation.name;
    const t = setTimeout(() => {
      setCurrentSpotlightName(target);
      setPendingSpotlight(null);
    }, 260);
    return () => clearTimeout(t);
  }, [pendingSpotlight]);

  const artistCount = favorites.length + discoveredArtists.length;
  const hasData = artistCount >= 4;
  const hasAnyArtists = artistCount > 0;
  const showHelperText = !user || artistCount < 10;

  // Show preview button when signed out or signed in with no artists
  const showPreview = !user || !hasAnyArtists;

  const handleOpenPreview = useCallback(() => {
    appDispatch({ type: VIEW_UNIVERSE_PREVIEW });
  }, [appDispatch]);

  // When showing the preview, render the preview CTA instead of normal universe
  if (showPreview) {
    return (
      <div className="universe-section">
        <p className="universe-helper-text">
          <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
            <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
          </svg>
          See how your personal music universe works
        </p>
        <div className="universe-preview-btn-wrap">
          <button className="universe-preview-btn" onClick={handleOpenPreview}>
            <span className="universe-preview-btn-icon">🌌</span>
            <span className="universe-preview-btn-label">Preview Universe</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="universe-section">
      {showHelperText && (
        <p className="universe-helper-text">
          <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
            <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
          </svg>
          Favorite and discover artists to grow your universe
        </p>
      )}

      {/* Signed in, not enough data */}
      {!hasData && universeStatus !== 'computing' && (
        <div className="universe-placeholder-wrap">
          <div className="section-placeholder-card empty">
            <span className="section-placeholder-text">
              Add favorites and discoveries to create your personal music universe
            </span>
          </div>
        </div>
      )}

      {/* Signed in, enough data but no universe yet */}
      {user && hasData && universeStatus === 'none' && (
        <div className="universe-build-prompt">
          <button className="universe-build-btn" onClick={refreshUniverse}>
            Build Your Universe
          </button>
          <span className="universe-build-hint">
            {favorites.length + discoveredArtists.length} artists ready to map
          </span>
        </div>
      )}

      {/* Computing */}
      {user && universeStatus === 'computing' && (
        <div className="universe-computing">
          <div className="universe-computing-spinner" />
          <span className="universe-computing-text">Mapping your music universe...{computePercent > 0 ? ` ${computePercent}%` : ''}</span>
        </div>
      )}

      {/* Error */}
      {user && universeStatus === 'error' && (
        <div className="universe-error">
          <span>Something went wrong building your universe.</span>
          <button className="universe-retry-btn" onClick={refreshUniverse}>
            Try again
          </button>
        </div>
      )}

      {/* Ready — show visualization snapshot */}
      {user && universeData && (universeStatus === 'ready' || universeStatus === 'stale') && (
        <div className="universe-content">
          <div className="universe-viz-container">
            <canvas ref={canvasRef} className="universe-viz-canvas" />
            <button className="universe-viz-explore-btn" onClick={handleExplore}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="16" height="16">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <span>Tap to explore</span>
            </button>
          </div>
          {displayedSpotlight && (
            <div className={`spotlight-swap${leaving ? ' leaving' : ''}`}>
              <SpotlightCard
                key={displayedSpotlight.recommendation.name}
                recommendation={displayedSpotlight.recommendation}
                clusterColor={displayedSpotlight.clusterColor}
                clusterLabel={displayedSpotlight.clusterLabel}
                onNext={handleSpotlightNext}
                hasNext={!!nextSpotlight && !leaving}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default MyUniverseSection;
